import { Building2, CreditCard, FileText, Hash, MessageSquare, ReceiptText, Undo2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

const groups = [
	{ key: "chargeback", icon: ReceiptText, fields: ["REASON_CODE", "FUNCTION_CODE", "FUNCTION_CODE_NAME", "CHB_PROC_DATE", "CHB_TR_DATE", "CHB_AMOUNT", "CHB_CCY", "CHB_SETTL_AMOUNT", "CHB_SETTL_CCY", "CHB_AMOUNT_TRANSACTION_CCY", "CHB_REFER_NUM", "DOC_INDICATOR", "COMMENTS", "RDR"] },
	{ key: "payment", icon: CreditCard, fields: ["PAYMENT_ID", "TR_AMOUNT", "TR_CCY", "TR_DATE_TIME", "CARD", "CARD_TYPE", "ISSUER_COUNTRY", "ECI_SLI"] },
	{ key: "merchant", icon: Building2, fields: ["MERCHANT_NAME", "MERCHANT_ID", "MERCHANT_LEGAL_NAME", "PARENT_MRCH_ID", "PARENT_MRCH_NAME", "MCC_CODE", "MERCHANT_IBAN_CODE", "CARD_ACCEPTOR_ID", "MAPPING_ACCOUNT_NUMBER"] },
	{ key: "settlement", icon: Undo2, fields: ["SETTLEMENT_DATE", "REV_INDICATOR", "REV_AMOUNT", "REV_CCY", "REV_DATE_TIME"] },
	{ key: "response", icon: MessageSquare, fields: ["MERCHANT_DECISION", "MERCHANT_RESP_DATE", "MERCHANT_COMMENTS"] },
	{ key: "references", icon: Hash, fields: ["ACQ_REF_NR", "ACQ_BIN", "TR_RET_REF_NR", "XID", "TLID"] },
	{ key: "other", icon: FileText, fields: [] },
] as const;

const fieldGroups = new Map<string, string>(groups.flatMap(group => group.fields.map(field => [field, group.key] as const)));
const fieldText = (value: unknown) => value === null || value === undefined ? "" : typeof value === "object" ? JSON.stringify(value, null, 2) : String(value);

const RecordLayout = styled.article`
	width: 100%; min-width: 0; container-type: inline-size; color: #344054;
	.overview { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; padding: 4px 0 2px; }
	.identity { display: flex; align-items: center; gap: 10px; min-width: 0; }
	.identity > svg { flex-shrink: 0; color: #167ca6; }
	h2 { margin: 0; font-size: 16px; line-height: 1.5; font-weight: 600; overflow-wrap: anywhere; }
	.record-count { margin: 0; padding: 4px 10px; border-radius: 6px; background: #f5f7fa; color: #667085; font-size: 12px; line-height: 1.5; }
	section { border: 1px solid #e5eaf0; border-radius: 10px; overflow: hidden; margin-top: 14px; }
	section > header { display: flex; align-items: center; gap: 8px; padding: 11px 16px; border-bottom: 1px solid #edf0f4; background: #fafbfc; }
	section > header svg { color: #7b88a1; flex-shrink: 0; }
	h3 { margin: 0; font-size: 13px; font-weight: 600; }
	dl { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 20px 24px; margin: 0; padding: 16px; }
	dl > div { min-width: 0; }
	dt { font-size: 10px; line-height: 1.5; color: #7b8697; letter-spacing: .03em; overflow-wrap: anywhere; }
	dd { margin: 5px 0 0; font-size: 13px; line-height: 1.5; font-weight: 500; font-variant-numeric: tabular-nums; white-space: pre-wrap; overflow-wrap: anywhere; }
	dd.empty { color: #a0a8b5; font-weight: 400; }
	@container (max-width: 850px) { dl { grid-template-columns: repeat(3, minmax(0, 1fr)); } }
	@container (max-width: 620px) { dl { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; } }
	@container (max-width: 360px) { dl { grid-template-columns: minmax(0, 1fr); } }
`;

export function FileRecordPreview({ row, headers }: { row: Record<string, unknown>; headers: string[] }) {
	const { t } = useTranslation();
	return <RecordLayout aria-label={t("filePreview.recordDetails")}>
		<header className="overview">
			<div className="identity"><FileText size={20} aria-hidden="true" /><h2>{t("filePreview.recordDetails")}</h2></div>
			<p className="record-count">{t("filePreview.singleRecord", { count: headers.length })}</p>
		</header>
		{groups.map(group => {
			const fields = headers.filter(field => (fieldGroups.get(field.toUpperCase()) ?? "other") === group.key);
			if (!fields.length) return null;
			return <section key={group.key} aria-label={t(`filePreview.groups.${group.key}`)}>
				<header><group.icon size={16} aria-hidden="true" /><h3>{t(`filePreview.groups.${group.key}`)}</h3></header>
				<dl>{fields.map(field => {
					const value = fieldText(row[field]);
					return <div key={field} data-field={field}><dt title={field}>{field.replaceAll("_", " ")}</dt><dd className={value === "" ? "empty" : undefined} title={value === "" ? t("filePreview.notProvided") : undefined}>{value === "" ? "—" : value}</dd></div>;
				})}</dl>
			</section>;
		})}
	</RecordLayout>;
}
